import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UpsertTouristicPlaceForm } from "../schemas";
import touristicPlacesQueryKeys, {
  acExpoTouristicPlacesQueryKeys,
} from "../query-keys/touristic-places.query-keys";
import { TouristicPlaceItemType } from "@ac-expo/api-types";
import { TouristicPlaceStatus } from "@prisma/client";

const getDataFromFormData = ({
  openings,
  exceptionalOpenings,
  venue,
  entry,
  ...form
}: UpsertTouristicPlaceForm) => {
  return {
    ...form,
    isIndoor: venue.indoor,
    isOutdoor: venue.outdoor,
    isPaidEntry: entry === "paid",
    openings: openings.map(({ dates, timetables, ...opening }) => ({
      ...opening,
      fromDate: dates?.from.toISOString(),
      toDate: dates?.to.toISOString(),
      timetables: timetables.map(
        ({
          openingTime,
          closingTime,
          breakEndTime,
          breakStartTime,
          ...timetable
        }) => ({
          ...timetable,
          openingTime: openingTime?.toString().slice(0, 5) ?? null,
          closingTime: closingTime?.toString().slice(0, 5) ?? null,
          breakStartTime: breakStartTime?.toString().slice(0, 5) ?? null,
          breakEndTime: breakEndTime?.toString().slice(0, 5) ?? null,
        })
      ),
    })),
    exceptionalOpenings: exceptionalOpenings.map(
      ({
        timetable: {
          openingTime,
          closingTime,
          breakEndTime,
          breakStartTime,
          ...timetable
        },
        ...exceptionalOpening
      }) => ({
        ...exceptionalOpening,
        timetable: {
          ...timetable,
          openingTime: openingTime?.toString().slice(0, 5),
          closingTime: closingTime?.toString().slice(0, 5),
          breakStartTime: breakStartTime?.toString().slice(0, 5),
          breakEndTime: breakEndTime?.toString().slice(0, 5),
        },
      })
    ),
  };
};

export const createTouristicTouristicPlace = async (
  body: UpsertTouristicPlaceForm
) => {
  try {
    const { data } = await privateAxios.post(
      "/touristic-places",
      getDataFromFormData(body)
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useCreateTouristicPlaceMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpsertTouristicPlaceForm) =>
      createTouristicTouristicPlace(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: touristicPlacesQueryKeys.me(),
      });
    },
  });
};

export const updateTouristicPlace = async (
  id: string,
  body: UpsertTouristicPlaceForm
) => {
  try {
    const { data } = await privateAxios.patch(
      `/touristic-places/${id}`,
      getDataFromFormData(body)
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useUpdateTouristicPlaceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpsertTouristicPlaceForm;
    }) => updateTouristicPlace(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: touristicPlacesQueryKeys.me(),
      });
      queryClient.invalidateQueries({
        queryKey: touristicPlacesQueryKeys.get(id),
      });
    },
  });
};

export const deleteTouristicPlace = async (id: string) => {
  try {
    const { data } = await privateAxios.delete(`/touristic-places/${id}`);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useDeleteTouristicPlaceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteTouristicPlace(id),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: touristicPlacesQueryKeys.me(),
      });
    },
  });
};

export const setTouristicPlaceOnlineStatus = async (
  id: string,
  isOnline: boolean
) => {
  try {
    const { data } = await privateAxios.patch(
      `/touristic-places/${id}/online`,
      {
        isOnline,
      }
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useSetTouristicPlaceOnlineStatusMutation = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ isOnline }: { isOnline: boolean }) =>
      setTouristicPlaceOnlineStatus(id, isOnline),
    onSuccess: (_, { isOnline }) => {
      queryClient.setQueryData(
        touristicPlacesQueryKeys.me(),
        (data: TouristicPlaceItemType[]) => {
          return data.map((touristicPlace) =>
            touristicPlace.id === id
              ? {
                  ...touristicPlace,
                  status: isOnline
                    ? TouristicPlaceStatus.beforeOnlineControl
                    : TouristicPlaceStatus.draft,
                }
              : touristicPlace
          );
        }
      );
    },
  });
};

const updateTouristicPlaceStatus = async (
  touristicPlaceId: string,
  status: TouristicPlaceStatus
) => {
  const { data } = await privateAxios.post(
    `/touristic-places/${touristicPlaceId}/update-status`,
    {
      status,
    }
  );
  return data;
};

export const useUpdateTouristicPlaceStatusMutation = (
  touristicPlaceId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (status: TouristicPlaceStatus) =>
      updateTouristicPlaceStatus(touristicPlaceId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: acExpoTouristicPlacesQueryKeys.all,
      });
    },
  });
};
